import React, { useState } from 'react';
import { Loader2, Package, Upload, X } from 'lucide-react';
import { TaskySelect } from '../tasky';
import {
  ProductSelectionModal,
  ProductSelectionData,
} from '../image-generation';
import lpWorkflowService, {
  CreateLpWorkflow,
  LpObjective,
  LpWorkflowProject,
} from '../../services/api/lpWorkflowService';
import type { LpGenerationMode } from '../../services/api/lpGenerationService';

export const LP_OBJECTIVES: Record<LpObjective, string> = {
  purchase: '購入',
  inquiry: '問い合わせ',
  download: '資料請求',
  booking: '予約',
  registration: '登録・申込',
};
const CTA_LABELS: Record<LpObjective, string> = {
  purchase: '購入する',
  inquiry: '問い合わせる',
  download: '資料を請求する',
  booking: '予約する',
  registration: '申し込む',
};
export default function LpWorkflowCreate({
  onCreated,
}: {
  onCreated: (project: LpWorkflowProject) => void;
}) {
  const [selection, setSelection] = useState<ProductSelectionData | null>(null);
  const [modal, setModal] = useState(false);
  const [info, setInfo] = useState('');
  const [objective, setObjective] = useState<LpObjective>('inquiry');
  const [ctaLabel, setCtaLabel] = useState(CTA_LABELS.inquiry);
  const [ctaUrl, setCtaUrl] = useState('');
  const [mode, setMode] = useState<LpGenerationMode>('decorative');
  const [uploads, setUploads] = useState<
    { name: string; data: string; category: string }[]
  >([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const choose = (value: ProductSelectionData) => {
    setSelection(value);
    const product = value.product;
    setInfo(
      [
        product.name,
        product.description,
        product.extended_metadata?.product?.usp,
      ]
        .filter(Boolean)
        .join('\n'),
    );
    setModal(false);
  };
  const upload = async (files: FileList | null) => {
    if (!files) return;
    setUploading(true);
    setError('');
    try {
      const next: { name: string; data: string; category: string }[] = [];
      for (const file of Array.from(files)) {
        if (
          !['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(
            file.type,
          ) ||
          file.size > 10 * 1024 * 1024
        )
          throw new Error(
            '画像は10MB以下のPNG・JPEG・WebP・GIFを選択してください。',
          );
        const data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        next.push({ name: file.name, data, category: 'material' });
      }
      setUploads((old) => [...old, ...next]);
    } catch (e) {
      setError(e instanceof Error ? e.message : '画像を読み込めませんでした。');
    } finally {
      setUploading(false);
    }
  };
  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || uploading || !info.trim()) return;
    setBusy(true);
    setError('');
    try {
      const images: CreateLpWorkflow['reference_images'] = (
        selection?.selectedImages ?? []
      ).map((img) => ({
        s3_key: img.file_path,
        category:
          img.category === 'brand_logo' ? 'brand_logo' : 'product_image',
      }));
      images.push(
        ...uploads.map((img) => ({
          image_data: img.data,
          category: img.category,
        })),
      );
      if (images.length > 8)
        throw new Error(
          '参照画像は商品画像とアップロードを合わせて8枚まで選択してください。',
        );
      const project = await lpWorkflowService.create({
        title: selection?.product.name,
        product_id: selection?.product.id,
        product_info: info,
        generation_mode: mode,
        objective,
        cta_label: ctaLabel,
        cta_url: ctaUrl,
        selection: selection
          ? {
              segment_id: selection.segmentId,
              segment_name: selection.segmentName,
              appeal_id: selection.appealId,
              appeal_name: selection.appealName,
              appeal_direction: selection.appealDirection,
              additional_context: selection.additionalContext,
            }
          : undefined,
        reference_images: images,
      });
      onCreated(project);
    } catch (e) {
      setError(
        e instanceof Error && !('response' in e)
          ? e.message
          : lpWorkflowService.error(e),
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <form className="lpw-create lpw-card" onSubmit={create}>
        <div className="lpw-kicker">商品に合わせて、ひとつずつ。</div>
        <h2>どんな商品を伝えるLPですか？</h2>
        <p className="lpw-muted">
          登録した商品からAIが理解を整理し、訴求・構成・コピー・デザインを順に提案します。
        </p>
        {error && (
          <div role="alert" className="lpw-error">
            {error}
          </div>
        )}
        <button
          className="lpw-button secondary"
          type="button"
          data-tutorial="ads-lp-product-select"
          onClick={() => setModal(true)}
          disabled={busy}
        >
          <Package size={16} />
          {selection
            ? `商品：${selection.product.name}`
            : '登録済みの商品を選ぶ'}
        </button>
        {selection && (
          <p className="lpw-selection">
            {[
              selection.segmentName,
              selection.appealName,
              selection.appealDirection,
            ]
              .filter(Boolean)
              .join(' ／ ') || '商品情報と素材を引き継ぎます'}
          </p>
        )}
        <label className="lpw-field">
          商品情報
          <textarea
            required
            maxLength={10000}
            value={info}
            onChange={(e) => setInfo(e.target.value)}
            rows={5}
            placeholder="商品の特徴、誰のどんな悩みを解消するか、価格や条件など。登録済みの商品を選ぶと自動で入ります。"
          />
        </label>
        <div className="lpw-grid-2">
          <label className="lpw-field">
            LPの目的
            <TaskySelect
              className="font-normal"
              value={objective}
              onChange={(e) => {
                const value = e.target.value as LpObjective;
                setObjective(value);
                setCtaLabel(CTA_LABELS[value]);
              }}
            >
              {Object.entries(LP_OBJECTIVES).map(([value, name]) => (
                <option key={value} value={value}>
                  {name}
                </option>
              ))}
            </TaskySelect>
          </label>
          <label className="lpw-field">
            ボタンの文言
            <input
              required
              maxLength={80}
              value={ctaLabel}
              onChange={(e) => setCtaLabel(e.target.value)}
            />
          </label>
        </div>
        <label className="lpw-field">
          ボタンの遷移先URL
          <input
            type="url"
            value={ctaUrl}
            maxLength={2000}
            onChange={(e) => setCtaUrl(e.target.value)}
            placeholder="https://...（後から設定できます）"
          />
        </label>
        <fieldset data-tutorial="ads-lp-mode" className="lpw-field">
          <legend>ページの見せ方</legend>
          <div className="lpw-grid-2">
            {(
              [
                [
                  'decorative',
                  '文章と画像を組み合わせる',
                  '読みやすい本文と商品画像。PC・スマホに対応。',
                ],
                [
                  'image2_overlay',
                  '画像を中心に見せる',
                  '縦に並ぶ画像と編集できる本文。各セクションに画像を配置。',
                ],
              ] as const
            ).map(([value, name, description]) => (
              <label
                className={`lpw-choice ${mode === value ? 'selected' : ''}`}
                key={value}
              >
                <input
                  type="radio"
                  name="lp-mode"
                  value={value}
                  checked={mode === value}
                  onChange={() => setMode(value)}
                />
                <strong>{name}</strong>
                <small>{description}</small>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="lpw-assets">
          {selection?.selectedImages.map((img) => (
            <img key={img.id} src={img.url} alt={img.title || img.file_name} />
          ))}
        </div>
        <label className="lpw-upload">
          <Upload size={16} />
          商品画像・ロゴ・参考素材を追加
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            multiple
            disabled={busy || uploading}
            onChange={(e) => {
              void upload(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
        {uploads.map((img, index) => (
          <div className="lpw-upload-row" key={`${img.name}-${index}`}>
            <span>{img.name}</span>
            <TaskySelect
              className="h-8 w-32 text-sm"
              aria-label={`${img.name}の種類`}
              value={img.category}
              onChange={(e) =>
                setUploads((old) =>
                  old.map((item, i) =>
                    i === index ? { ...item, category: e.target.value } : item,
                  ),
                )
              }
            >
              <option value="material">参考素材</option>
              <option value="product_image">商品画像</option>
              <option value="brand_logo">ブランドロゴ</option>
            </TaskySelect>
            <button
              type="button"
              aria-label={`${img.name}を削除`}
              onClick={() =>
                setUploads((old) => old.filter((_, i) => i !== index))
              }
            >
              <X size={16} />
            </button>
          </div>
        ))}
        <button
          className="lpw-button primary"
          data-tutorial="ads-lp-generate"
          disabled={busy || uploading || !info.trim()}
        >
          {busy && <Loader2 size={16} className="animate-spin" />}
          商品理解から始める
        </button>
      </form>
      <ProductSelectionModal
        isOpen={modal}
        onClose={() => setModal(false)}
        onConfirm={choose}
      />
    </>
  );
}
